import { GRID_CONFIG, sortByAuditUpdatedOn } from "../config";
import axios from "axios";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import {
    FaPencilAlt,
    FaPlusCircle,
    FaUsers,
    FaFileAlt,
    FaEllipsisV,
    FaTrash,
    FaSearch,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import "@fortawesome/fontawesome-free/css/all.min.css";

import { notifyUser } from "../utils/notifyUser";

const SecurityGroups = ({ user }) => {
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [groupName, setGroupName] = useState("");
    const [comment, setComment] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [showActions, setShowActions] = useState(false);
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const email = user?.email;
    const actionRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchGroups();
    }, []);

    const fetchGroups = () => {
        axios
            .get(`${process.env.REACT_APP_BACKEND_URL}/api/security-groups`, {
                withCredentials: true,
            })
            .then((res) => setGroups(sortByAuditUpdatedOn(res.data)))
            .catch((err) => console.error("Error loading groups", err));
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const filteredGroups = groups.filter(
        (group) =>
            group.group_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (group.description &&
                group.description
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())),
    );

    const handleExportGroupsCSV = () => {
        try {
            if (!filteredGroups || filteredGroups.length === 0) {
                notifyUser(null, "info", "No groups to export", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }

            const rows = filteredGroups.map((g) => ({
                GroupName: g.group_name || "",
                Description: g.description || "",
                CreatedBy: g.audit_created_by || "",
                CreatedOn: g.audit_created_on
                    ? new Date(g.audit_created_on).toLocaleString()
                    : "",
                UpdatedBy: g.audit_updated_by || "",
                UpdatedOn: g.audit_updated_on
                    ? new Date(g.audit_updated_on).toLocaleString()
                    : "",
            }));

            const header = Object.keys(rows[0]).join(",");
            const csvBody = rows
                .map((r) =>
                    Object.values(r)
                        .map((v) => `"${String(v).replace(/\"/g, '""')}"`)
                        .join(","),
                )
                .join("\n");

            const csv = `${header}\n${csvBody}`;
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `security-groups-${new Date().toISOString().slice(0, 10)}.csv`,
            );
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            notifyUser(null, "error", "Failed to export groups", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const handleCreateGroup = () => {
        if(!groupName.trim()) {
            notifyUser(null, "error", "Group Name is required", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        axios
            .post(
                `${process.env.REACT_APP_BACKEND_URL}/api/security-groups`,
                {
                    group_name: groupName,
                    description: comment,
                    audit_created_by: email,
                },
                { withCredentials: true },
            )
            .then(() => {
                notifyUser(null, "success", "Group created successfully!", "toast", null, null, null, null, null, false, false, "top-center");
                resetForm();
                fetchGroups();
            })
            .catch(() =>
                notifyUser(null, "error", "Group creation failed: A group with this name already exists.", "toast", null, null, null, null, null, false, false, "top-center"),);
    };

    const handleEdit = (group) => {
        setSelectedGroup(group);
        setGroupName(group?.group_name);
        setComment(group?.description);
    };

    const handleEditClick = () => setShowForm(true);

    const handleSaveEdit = () => {
        if(!groupName.trim()) {
            notifyUser(null, "error", "Group Name is required", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        axios
            .put(
                `${process.env.REACT_APP_BACKEND_URL}/api/security-groups/${selectedGroup.id}`,
                {
                    group_name: groupName,
                    description: comment,
                    audit_updated_by: email,
                },
                { withCredentials: true },
            )
            .then(() => {
                notifyUser(null, "success", "Group updated successfully!", "toast", null, null, null, null, null, false, false, "top-center");
                resetForm();
                fetchGroups();
            })
            .catch(() => notifyUser(null, "error", "Failed to update group", "toast", null, null, null, null, null, false, false, "top-center"));
    };

    const handleDeleteGroup = async () => {
        if(!selectedGroup) {
            notifyUser(null, "error", "Please select a group to delete.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const result = await Swal.fire({
            title: "Are you sure?",
            text: `You are about to delete group "${selectedGroup.group_name}". This action cannot be undone!`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel",
        });

        if(result.isConfirmed) {
            try {
                await axios.delete(
                    `${process.env.REACT_APP_BACKEND_URL}/api/security-groups/${selectedGroup.id}`,
                    { withCredentials: true },
                );
                notifyUser(null, "success", "Group deleted successfully!", "toast", null, null, null, null, null, false, false, "top-center");
                resetForm();
                fetchGroups();
            } catch(err) {
                console.error("Error deleting group:", err);
                notifyUser(null, "error", "Failed to delete group.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        }
    };

    const handleAction = (actionType) => {
        if(!selectedGroup) {
            notifyUser(null, "error", "Please select a group first", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        const menus = JSON.parse(selectedGroup.menu_list || "[]");
        if(actionType === "AddPages" || menus.length)
            navigate(`/${actionType.toLowerCase()}/${selectedGroup.id}`);
        else notifyUser(null, "error", "Add page to the group first", "toast", null, null, null, null, null, false, false, "top-center");
    };

    const resetForm = () => {
        setSelectedGroup(null);
        setGroupName("");
        setComment("");
        setShowForm(false);
    };

    const toggleActions = () => setShowActions((prev) => !prev);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if(
                actionRef.current &&
                !actionRef.current.contains(event.target)
            ) {
                setShowActions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const cannotRemoveGroups = (
        process.env.REACT_APP_CANNOT_REMOVE_SECURITY_GROUPS || ""
    )
        .split(",")
        .map((id) => parseInt(id.trim()));

    const customStyles = {
        table: {
            style: {
                borderRadius: "8px",
                overflow: "hidden",
            },
        },
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
                fontSize: "16px",
                minHeight: "50px",
            },
        },
        rows: {
            style: {
                minHeight: "50px",
                borderBottom: "1px solid #C2D7E2",
            },
        },
        cells: {
            style: {
                fontSize: "14px",
                padding: "12px 20px",
            },
        },
        pagination: {
            style: {
                borderTop: "none",
                padding: "8px 16px",
            },
        },
    };

    const columns = [
        {
            name: "Group Name",
            selector: (row) => row.group_name,
            sortable: true,
        },
        {
            name: "Description",
            selector: (row) => row.description,
            sortable: true,
            cell: (row) => (
                <div
                    style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "300px",
                        cursor: "pointer",
                    }}
                    title={row.description}
                >
                    {row.description}
                </div>
            ),
        },
        {
            name: " Created By",
            selector: (row) => row.audit_created_by || "N/A",
            sortable: true,
        },
        {
            name: " Created On",
            selector: (row) =>
                row.audit_created_on
                    ? new Date(row.audit_created_on).toLocaleString()
                    : "N/A",
            sortable: true,
        },
        {
            name: " Updated By",
            selector: (row) => row.audit_updated_by || "—",
            sortable: true,
        },
        {
            name: " Updated On",
            selector: (row) =>
                row.audit_updated_on
                    ? new Date(row.audit_updated_on).toLocaleString()
                    : "—",
            sortable: true,
        },
    ];

    const paginatedGroups = filteredGroups.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage,
    );

    return (
        <div
            className="aqi-grid-page"
            style={{
                padding: "30px",
                width: "90%",
                margin: "30px auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                backgroundColor: "white",
                position: "relative",
            }}
        >
            <div>
                <h2
                    style={{
                        color: "var(--navy)",
                        marginBottom: "10px",
                        fontSize: "large",
                        fontFamily: "large",
                    }}
                >
                    SECURITY GROUPS
                </h2>

                <div className="aqi-grid-toolbar">
                    <div>
                        <select
                            style={{
                                padding: "0.375rem 2rem 0.375rem 0.75rem",
                                color: "var(--navy)",
                                backgroundColor: "#F5F7FA",
                                border: "2px solid #C2D7E2",
                                borderRadius: "0.375rem",
                                fontWeight: "500",
                                appearance: "none",
                                backgroundImage:
                                    "var(--navy-select-chevron)",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 0.5rem center",
                                backgroundSize: "16px 12px",
                                transition: "all 0.2s ease",
                                cursor: "pointer",
                                outline: "none",
                            }}
                            className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                            value={perPage}
                            onChange={(e) => {
                                setPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            {GRID_CONFIG.pageSizeOptions.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>

                    <div className="aqi-grid-search">
                            <FaSearch
                                className="absolute left-3 top-2.5"
                                style={{ color: "var(--navy)" }}
                            />
                            <input
                                type="text"
                                placeholder="Search groups..."
                                value={searchTerm}
                                onChange={handleSearch}
                                style={{
                                    paddingLeft: "2.5rem",
                                    paddingRight: "0.75rem",
                                    paddingTop: "0.5rem",
                                    paddingBottom: "0.5rem",
                                    borderRadius: "0.375rem",
                                    border: "2px solid #C2D7E2",
                                    backgroundColor: "#F5F7FA",
                                    transition: "all 0.2s ease",
                                    outline: "none",
                                    color: "var(--navy)",
                                }}
                                className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                            />
                    </div>
                    <div className="aqi-grid-toolbar-actions aqi-grid-toolbar-actions--export-last">
                        <button
                            className="aqi-grid-button aqi-grid-button--primary"
                            onClick={() => {
                                setSelectedGroup(null);
                                setGroupName("");
                                setComment("");
                                setShowForm(true);
                            }}
                        >
                            <FaPlusCircle /> Create
                        </button>
                        {selectedGroup && (
                            <>
                                <button
                                    className="aqi-grid-button aqi-grid-button--primary"
                                    onClick={handleEditClick}
                                >
                                    <FaPencilAlt /> Edit
                                </button>
                                <button
                                    className="aqi-grid-button aqi-grid-button--danger"
                                    onClick={handleDeleteGroup}
                                    disabled={cannotRemoveGroups.includes(selectedGroup.id)}
                                >
                                    <FaTrash size={16} /> Delete
                                </button>
                                <div className="relative" ref={actionRef}>
                                    <button
                                        className="aqi-grid-button aqi-grid-button--warning"
                                        onClick={toggleActions}
                                    >
                                        <FaEllipsisV /> Actions
                                    </button>
                                    {showActions && (
                                        <div
                                            style={{
                                                position: "absolute",
                                                right: 0,
                                                top: "38px",
                                                background: "white",
                                                border: "1px solid #ccc",
                                                borderRadius: "5px",
                                                zIndex: 10,
                                                minWidth: "150px",
                                            }}
                                        >
                                            <button
                                                style={dropdownButtonStyle}
                                                onClick={() => handleAction("AddPeople")}
                                            >
                                                <FaUsers style={{ marginRight: "6px" }} />
                                                Manage Users
                                            </button>
                                            <button
                                                style={{
                                                    ...dropdownButtonStyle,
                                                    whiteSpace: "nowrap",
                                                }}
                                                onClick={() => handleAction("AddPages")}
                                            >
                                                <FaFileAlt
                                                    style={{
                                                        marginRight: "3px",
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                Manage Resources
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        <button
                            onClick={handleExportGroupsCSV}
                            title="Export CSV"
                            className="aqi-grid-button aqi-grid-button--primary"
                        >
                            Export
                        </button>
                    </div>
                </div>

                <DataTable
                    columns={columns}
                    data={paginatedGroups}
                    customStyles={customStyles}
                    highlightOnHover
                    pointerOnHover
                    onRowClicked={handleEdit}
                    striped
                    pagination={false}
                    conditionalRowStyles={[
                        {
                            when: (row) =>
                                selectedGroup && selectedGroup.id === row.id,
                            style: {
                                backgroundColor: "#C2D7E2",
                                fontWeight: "bold",
                                fontSize: "0.9rem",
                            },
                        },
                    ]}
                />

                <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
                    <span>
                        Showing{" "}
                        {filteredGroups.length === 0
                            ? 0
                            : (currentPage - 1) * perPage + 1}{" "}
                        to{" "}
                        {Math.min(currentPage * perPage, filteredGroups.length)}{" "}
                        of {filteredGroups.length} records
                        {searchTerm &&
                            ` (filtered from ${groups.length} total)`}
                    </span>
                    <div className="flex gap-2 text-lg items-center">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(1)}
                            className="disabled:opacity-40"
                        >
                            <FaAngleDoubleLeft />
                        </button>
                        <button
                            disabled={currentPage === 1}
                            onClick={() =>
                                setCurrentPage((prev) => Math.max(prev - 1, 1))
                            }
                            className="disabled:opacity-40"
                        >
                            <FaAngleLeft />
                        </button>
                        <span className="text-sm px-2">
                            Page {currentPage} of{" "}
                            {Math.ceil(filteredGroups.length / perPage)}
                        </span>
                        <button
                            disabled={
                                currentPage ===
                                Math.ceil(filteredGroups.length / perPage)
                            }
                            onClick={() =>
                                setCurrentPage((prev) =>
                                    Math.min(
                                        prev + 1,
                                        Math.ceil(
                                            filteredGroups.length / perPage,
                                        ),
                                    ),)
                            }
                            className="disabled:opacity-40"
                        >
                            <FaAngleRight />
                        </button>
                        <button
                            disabled={
                                currentPage ===
                                Math.ceil(filteredGroups.length / perPage)
                            }
                            onClick={() =>
                                setCurrentPage(
                                    Math.ceil(filteredGroups.length / perPage),
                                )
                            }
                            className="disabled:opacity-40"
                        >
                            <FaAngleDoubleRight />
                        </button>
                    </div>
                </div>

                {showForm && (
                    <div
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0,0,0,0.5)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 1000,
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: "white",
                                padding: "20px",
                                borderRadius: "8px",
                                width: "400px",
                                boxShadow: "0 0 20px rgba(0,0,0,0.2)",
                            }}
                        >
                            <h3 style={{ marginBottom: "10px" }}>
                                {selectedGroup
                                    ? "Edit Security Group"
                                    : "Create Security Group"}
                            </h3>
                            <input
                                type="text"
                                placeholder="Group Name"
                                value={groupName}
                                onChange={(e) => setGroupName(e.target.value)}
                                style={{
                                    width: "100%",
                                    marginBottom: "10px",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    border: "1px solid #ccc",
                                }}
                            />
                            <textarea
                                placeholder="Comment"
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                style={{
                                    width: "100%",
                                    marginBottom: "10px",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    border: "1px solid #ccc",
                                }}
                            />
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: "10px",
                                }}
                            >
                                <button
                                    onClick={() => setShowForm(false)}
                                    style={buttonStyle("#6c757d")}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={
                                        selectedGroup
                                            ? handleSaveEdit
                                            : handleCreateGroup
                                    }
                                    style={buttonStyle("#007bff")}
                                >
                                    {selectedGroup
                                        ? "Edit Group"
                                        : "Create Group"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const buttonStyle = (color) => ({
    display: "flex",
    alignItems: "center",
    border: `1px solid ${color}`,
    color: color,
    padding: "8px 12px",
    fontSize: "14px",
    borderRadius: "4px",
    background: "white",
    cursor: "pointer",
    transition: "all 0.2s ease",
});

const dropdownButtonStyle = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "10px",
    border: "none",
    background: "white",
    fontSize: "14px",
    textAlign: "left",
    cursor: "pointer",
    borderBottom: "1px solid #C2D7E2",
};

SecurityGroups.propTypes = {
    user: PropTypes.shape({
        email: PropTypes.string,
    }),
};

export default SecurityGroups;
